import { getBaseRelationshipType } from './relationshipMappings';

/**
 * FAMILY ENGINE v7.2 - Relationship Refinement
 * 
 * CHANGELOG from v7.1:
 * - Fixed a major bug where `parental_sibling_in_law+spouse` was mapping to biological `parental_sibling` 
 *   (e.g., Uncle-in-law's wife becoming biological Aunt). This caused entire branches of in-laws 
 *   to be hallucinated as biological relatives.
 * - Added downward in-law routing (`sibling_in_law+child` -> `sibling_child_in_law`) to ensure nieces 
 *   and nephews on the in-law side map correctly.
 */

const normalize = (t) => {
  if (!t) return '';
  return getBaseRelationshipType(t);
};

const isStepOrHalf = (type) => {
  if (!type) return false;
  const t = type.toLowerCase();
  return t.includes('step') || t.includes('half');
};

const isStep = (type) => {
  if (!type) return false;
  return type.toLowerCase().includes('step');
};

const isAllowedStepOutput = (type) => {
  if (!type) return false;
  const allowed = [
    'step_parent',
    'step_child', 
    'step_sibling',
    'spouse',
    'parent_in_law',
    'child_in_law',
    'sibling_in_law'
  ];
  return allowed.includes(type);
};

const isStepOutput = (type) => {
  if (!type) return false;
  return type.toLowerCase().includes('step');
};

const priority = {
  'spouse': 1,
  'parent': 2,
  'child': 2,
  'sibling': 3,
  'grandparent': 4,
  'grandchild': 4,
  'parental_sibling': 5, 
  'sibling_child': 5,    
  'cousin': 6,
  'great_grandparent': 7,
  'great_grandchild': 7,
  'grandparent_sibling': 8,
  'grandsibling_child': 8,
  'cousin_once_removed': 9,
  'second_cousin': 10,
  'third_cousin': 11
};

const getPriority = (type) => priority[type] || 99;

export const generateSmartFamilyRelationships = (people, relationships) => {
  console.log(`[FamilyEngine v7.2] Analyzing ${people.length} people and ${relationships.length} relationships`);
  
  const peopleMap = new Map();
  people.forEach(p => {
    peopleMap.set(String(p.id), { ...p, links: [] });
  });

  relationships.forEach(rel => {
    const aId = String(rel.person_a_id);
    const bId = String(rel.person_b_id);
    const pA = peopleMap.get(aId);
    const pB = peopleMap.get(bId);
    if (!pA || !pB) return;

    pA.links.push({ to: bId, type: rel.relationship_type_b });
    pB.links.push({ to: aId, type: rel.relationship_type });
  });

  const existingPairs = new Set();
  relationships.forEach(rel => {
    const ids = [String(rel.person_a_id), String(rel.person_b_id)].sort();
    existingPairs.add(ids.join('_'));
  });

  const results = [];

  people.forEach(startPerson => {
    const startId = String(startPerson.id);
    const queue = [{ id: startId, path: [], depth: 0, hasStepOrHalf: false }];
    const visited = new Map([[startId, 0]]);

    while (queue.length > 0) {
      const { id, path, depth, hasStepOrHalf } = queue.shift();
      
      if (hasStepOrHalf && depth >= 2) continue;
      if (depth >= 5) continue;

      const node = peopleMap.get(id);
      if (!node) continue;

      for (const edge of node.links) {
        const targetId = String(edge.to);
        const edgeHasStep = isStepOrHalf(edge.type);
        const newPathHasStep = hasStepOrHalf || edgeHasStep;
        const newPath = [...path, edge.type];
        
        if (!visited.has(targetId) || visited.get(targetId) >= depth + 1) {
          visited.set(targetId, depth + 1);
          
          if (startId !== targetId) {
            const inferredType = evaluatePath(newPath, newPathHasStep);
            
            if (inferredType) {
              const pairKey = [startId, targetId].sort().join('_');
              if (!existingPairs.has(pairKey)) {
                results.push({
                  person_a_id: startPerson.id,
                  person_b_id: targetId,
                  relationshipType: inferredType,
                  trace: newPath
                });
              }
            }
          }
          
          queue.push({ 
            id: targetId, 
            path: newPath, 
            depth: depth + 1,
            hasStepOrHalf: newPathHasStep
          });
        }
      }
    }
  });

  const isBio = (type) => !type.includes('in_law') && !type.includes('step') && !type.includes('half_') && !type.includes('ex_');

  const finalMatches = new Map();
  results.forEach(res => {
    const pairKey = [String(res.person_a_id), String(res.person_b_id)].sort().join('_');
    const existing = finalMatches.get(pairKey);
    
    if (!existing) {
      finalMatches.set(pairKey, res);
    } else if (res.trace.length < existing.trace.length) {
      finalMatches.set(pairKey, res);
    } else if (res.trace.length === existing.trace.length) {
      const resBio = isBio(res.relationshipType);
      const existingBio = isBio(existing.relationshipType);
      if (resBio && !existingBio) {
        finalMatches.set(pairKey, res);
      } else if (resBio === existingBio) {
        if (getPriority(res.relationshipType) < getPriority(existing.relationshipType)) {
          finalMatches.set(pairKey, res);
        }
      }
    }
  });

  const output = Array.from(finalMatches.values());
  console.log(`[FamilyEngine v7.2] Found ${output.length} inferred relationships`);
  return output;
};

const evaluatePath = (path, pathHasStepOrHalf = false) => {
  if (path.length < 2) return null;

  if (!pathHasStepOrHalf) {
    pathHasStepOrHalf = path.some(edge => isStepOrHalf(edge));
  }

  let current = normalize(path[0]);

  for (let i = 1; i < path.length; i++) {
    const next = normalize(path[i]);

    if (pathHasStepOrHalf && path.length > 2) {
      return null;
    }

    const key = `${current}+${next}`;
    
    const matrix = {
      // --- DIRECT LINEAGE ---
      'parent+parent': 'grandparent',
      'parent+child': 'sibling',
      'parent+sibling': 'parental_sibling',
      'child+parent': 'spouse', 
      'child+child': 'grandchild',
      'child+sibling': 'child',
      'sibling+parent': 'parent',
      'sibling+child': 'sibling_child',
      'sibling+sibling': 'sibling',

      // --- SPOUSE AS PARENT ---
      'parent+spouse': 'parent',
      'spouse+child': 'child',

      // --- SECOND DEGREE ---
      'grandparent+parent': 'great_grandparent',
      'grandparent+child': 'parental_sibling',
      'grandparent+sibling': 'grandparent_sibling',
      'grandchild+child': 'great_grandchild',
      'grandchild+sibling': 'grandchild',
      
      // --- UNCLE / AUNT / NIECE / NEPHEW ---
      'parental_sibling+parent': 'grandparent',
      'parental_sibling+child': 'cousin',
      'parental_sibling+sibling': 'parental_sibling',
      'sibling_child+child': 'grandsibling_child',
      'sibling_child+sibling': 'sibling_child',
      
      // --- COUSIN LOGIC ---
      'cousin+child': 'cousin_once_removed',
      'cousin+sibling': 'cousin',
      'parent+cousin': 'cousin_once_removed',
      'child+cousin': 'sibling_child',
      'sibling+cousin': 'cousin',

      // --- EXTENDED COUSINS ---
      'cousin_once_removed+child': 'second_cousin',
      'parent+cousin_once_removed': 'second_cousin',
      'second_cousin+parent': 'cousin_once_removed',
      'child+second_cousin': 'cousin_once_removed',
      'second_cousin+sibling': 'second_cousin',
      'sibling+second_cousin': 'second_cousin',
      'second_cousin+child': 'third_cousin',
      'parent+second_cousin': 'third_cousin',
      'third_cousin+sibling': 'third_cousin',
      'sibling+third_cousin': 'third_cousin',

      // --- SIBLING TRANSITIVES ---
      'sibling+parental_sibling': 'parental_sibling',
      'sibling+grandparent': 'grandparent',
      
      // --- GRAND-TRANSITIVES ---
      'parent+parental_sibling': 'grandparent_sibling',
      'parent+grandsibling_child': 'cousin_once_removed',
      'grandparent_sibling+child': 'cousin_once_removed',

      // --- SPOUSE & IN-LAW LOGIC ---
      'spouse+spouse': 'spouse', 
      'spouse+parent': 'parent_in_law',
      'child+spouse': 'child_in_law',
      'spouse+sibling': 'sibling_in_law',
      'sibling+spouse': 'sibling_in_law',
      
      'spouse+grandparent': 'grandparent_in_law',
      'grandchild+spouse': 'grandchild_in_law',

      'spouse+parental_sibling': 'parental_sibling_in_law',
      'parental_sibling+spouse': 'parental_sibling_in_law',
      'spouse+sibling_child': 'sibling_child_in_law',
      'sibling_child+spouse': 'sibling_child_in_law',
      
      'spouse+grandparent_sibling': 'grandparent_sibling_in_law',
      'grandparent_sibling+spouse': 'grandparent_sibling_in_law',
      'spouse+grandsibling_child': 'grandsibling_child_in_law',
      'grandsibling_child+spouse': 'grandsibling_child_in_law',
      
      'spouse+cousin': 'cousin_in_law',
      'cousin+spouse': 'cousin_in_law',

      // Complex In-Law Transitives
      'parent_in_law+child': 'sibling_in_law',
      'parent+child_in_law': 'sibling_in_law',
      'parent_in_law+sibling': 'parental_sibling_in_law',
      'sibling+child_in_law': 'sibling_child_in_law',
      'sibling_in_law+child': 'sibling_child_in_law',
      'parental_sibling_in_law+child': 'cousin_in_law',

      // Reverse in-law to biological via spouse
      'spouse+parent_in_law': 'parent',
      'child_in_law+spouse': 'child',
      'spouse+grandparent_in_law': 'grandparent',
      'grandchild_in_law+spouse': 'grandchild',
      'parental_sibling_in_law+spouse': 'parental_sibling_in_law', // Fixed!
      'sibling_child_in_law+spouse': 'sibling_child_in_law', // Fixed!
      'grandparent_sibling_in_law+spouse': 'grandparent_sibling_in_law', // Fixed!
      'grandsibling_child_in_law+spouse': 'grandsibling_child_in_law', // Fixed!
      
      // --- MISSING BIOLOGICAL LINKS VIA SPOUSE ---
      'grandparent+spouse': 'grandparent',
      'spouse+grandchild': 'grandchild',
      'great_grandparent+spouse': 'great_grandparent',
      'spouse+great_grandchild': 'great_grandchild',
      
      // Ambiguous in-law defaults
      'parent_in_law+spouse': 'parent_in_law',
      'spouse+child_in_law': 'child_in_law',
      'spouse+parental_sibling_in_law': 'parental_sibling_in_law',
      'spouse+sibling_child_in_law': 'sibling_child_in_law',
      'spouse+grandparent_sibling_in_law': 'grandparent_sibling_in_law',
      'spouse+grandsibling_child_in_law': 'grandsibling_child_in_law',
      'cousin_in_law+spouse': 'cousin_in_law',
      'spouse+cousin_in_law': 'cousin_in_law',
    };

    if (matrix[key]) {
      current = matrix[key];
    } else {
      return null;
    }
  }

  // Handle Step-Sibling conversion
  if (pathHasStepOrHalf && current === 'sibling') {
    current = 'step_sibling';
  }

  const pathHasStep = path.some(edge => isStep(edge));
  if (pathHasStep && !isAllowedStepOutput(current)) {
    return null;
  }

  if (isStepOutput(current) && !isAllowedStepOutput(current)) {
    return null;
  }

  return current;
};

export const relationshipExists = (id1, id2, rels) => {
  const s1 = String(id1), s2 = String(id2);
  return rels.some(r => 
    (String(r.person_a_id) === s1 && String(r.person_b_id) === s2) ||
    (String(r.person_a_id) === s2 && String(r.person_b_id) === s1)
  );
};